import { Rectangle, Ticker } from "pixi.js"
import Entity, { CollisionType, Direction } from "./Entity"
import Level from "./Level"
import { PLAYER_WIDTH, PLAYER_HEIGHT, SCALE_FACTOR } from "./config"
import { getAnimation } from "./spritesheet"
import Bomb from "./Bomb"
import PowerUp from "./PowerUp"
import PowerUpCollection from "./PowerUpCollection"
import Clock, { ClockCallbackObject } from "./Clock"
import Poison, { PoisonEnum } from "./Poison"
import { PlayerId } from "$types/index"

export default class Player extends Entity {
  static PLAYER_WIDTH = PLAYER_WIDTH * SCALE_FACTOR
  static PLAYER_HEIGHT = PLAYER_HEIGHT * SCALE_FACTOR
  static PLAYER_KICK_TIMEOUT = 20
  static PLAYER_STARTING_SPEED = 5
  static PLAYER_STARTING_BOMBS = 1
  static PLAYER_STARTING_FIRE = 2

  private _disconnectedBlinkInterval: ClockCallbackObject | null = null
  get connected() {
    return this._disconnectedBlinkInterval === null
  }
  set connected(value: boolean) {
    if (value === this.connected) return

    if (value) {
      if (this._disconnectedBlinkInterval) {
        Clock.clear(this._disconnectedBlinkInterval)
        this._disconnectedBlinkInterval = null
      }
      this.alpha = 1
    } else {
      this._disconnectedBlinkInterval = Clock.setInterval(() => {
        this.alpha = this.alpha === 1 ? 0.5 : 1
      }, 100)
    }
  }

  movementEnabled: boolean = true

  invulnerable = false

  private _playerId: PlayerId
  get playerId() {
    return this._playerId
  }

  private _kickedBomb: Bomb | null = null
  get kickedBomb() {
    return this._kickedBomb
  }
  private _kickTimeout: ClockCallbackObject | null = null

  private _currentlyPlacedBombs = 0

  private _powerUpCollection: PowerUpCollection

  private _poison: PoisonEnum | null = null
  get poison() {
    return this._poison
  }

  get hasKick() {
    return this._powerUpCollection.powerUpCount.powerUpKick > 0
  }
  get hasPunch() {
    return this._powerUpCollection.powerUpCount.powerUpPunch > 0
  }
  get maxBombCount() {
    return this._powerUpCollection.powerUpCount.powerUpBomb + Player.PLAYER_STARTING_BOMBS
  }
  get fireSize() {
    if (this.poison === PoisonEnum.highPower) {
      return Poison.POISON_MAX_FIRE
    } else if (this.poison === PoisonEnum.lowPower) {
      return Poison.POISON_MIN_FIRE
    } else {
      return this._powerUpCollection.powerUpCount.powerUpFire + Player.PLAYER_STARTING_FIRE
    }
  }

  set currentlyPlacedBombs(value: number) {
    if (value < 0) {
      this._currentlyPlacedBombs = 0
    } else if (value > this.maxBombCount) {
      this._currentlyPlacedBombs = this.maxBombCount
    } else {
      this._currentlyPlacedBombs = value
    }
  }
  get currentlyPlacedBombs() {
    return this._currentlyPlacedBombs
  }

  get speed() {
    if (this.poison === PoisonEnum.diarrhea) {
      return Poison.POISON_DIARRHEA_SPEED
    }

    if (this.poison === PoisonEnum.slowed) {
      return Poison.POISON_SLOWED_SPEED
    }

    return this._speed + this._powerUpCollection.powerUpCount.powerUpSkates * PowerUp.SKATES_SPEED_INCREMENT
  }

  recentlyPlacedBomb: Bomb | null = null

  constructor(params: { playerId: PlayerId, x: number, y: number, level: Level }) {
    super({
      x: params.x,
      y: params.y,
      level: params.level,
      speed: Player.PLAYER_STARTING_SPEED,
      width: Player.PLAYER_WIDTH,
      height: Player.PLAYER_HEIGHT,
      textures: getAnimation(`player${params.playerId}WalkDown`),
      animations: {
        Up: getAnimation(`player${params.playerId}WalkUp`),
        Down: getAnimation(`player${params.playerId}WalkDown`),
        Left: getAnimation(`player${params.playerId}WalkLeft`),
        Right: getAnimation(`player${params.playerId}WalkRight`)
      }
    })
    this.setAtTileCoords(this.x, this.y)
    this._playerId = params.playerId

    this.pivot.set(0, this.height / 6.5)

    this.setBoundingBox(new Rectangle(0, 0, Level.LEVEL_TILE_SIZE, Level.LEVEL_TILE_SIZE))

    const hitBoxPadding = SCALE_FACTOR / 2.5
    const hitBoxPivotX = this.width / 3.5
    const hitBoxPivotY = this.height / 4
    this.setHitBox(new Rectangle(
      hitBoxPivotX + hitBoxPadding,
      hitBoxPivotY + hitBoxPadding,
      Level.LEVEL_TILE_SIZE / 2 - hitBoxPadding * 3,
      Level.LEVEL_TILE_SIZE / 2 - hitBoxPadding * 3
    ))

    this.stopMoving()

    this._powerUpCollection = new PowerUpCollection({
      powerUpBomb: 0,
      powerUpFire: 0,
      powerUpKick: 0,
      powerUpPunch: 0,
      powerUpSkates: 0
    })

    this.connected = false
  }

  tickUpdate(ticker: Ticker): void {
    super.tickUpdate(ticker)

    if (this.recentlyPlacedBomb && !this.checkCollision(this.recentlyPlacedBomb, CollisionType.BoundingBox)) {
      this.recentlyPlacedBomb = null
    }

    if (!this.alive) return

    if (this._poison === PoisonEnum.diarrhea) {
      this.placeBomb()
    }

  }

  startKick(bomb: Bomb) {
    if (!this.alive) return
    if (!this.hasKick) return
    this._kickedBomb = bomb
    this._kickTimeout = Clock.setTimeout(() => {
      if (this.processKick()) {
        this._kickedBomb!.startMoving(this.direction)
      }
      this.stopKick()
    }, Player.PLAYER_KICK_TIMEOUT)
  }

  stopKick() {
    this._kickedBomb = null
    if (this._kickTimeout) {
      Clock.clear(this._kickTimeout!)
    }
  }

  processKick() {
    if (this._kickedBomb && this.isFacingEntity(this._kickedBomb) && !this.isInTheSameTile(this._kickedBomb)) {
      return true
    }

    this.stopKick()
    return false
  }

  isBlocking(entityConstructor: typeof Entity.constructor): boolean {
    if (entityConstructor === Bomb) {
      return true
    }
    return false
  }

  placeBomb(): void {
    if (!this.movementEnabled) return
    if (!this.alive) return
    if (this._currentlyPlacedBombs >= this.maxBombCount) return
    if (this._poison === PoisonEnum.constipation) return
    if (this.level.getEntitiesAtTileCoords(this.getTileCoords()).some(e => e instanceof Bomb)) return

    const bomb = new Bomb({
      x: this.x,
      y: this.y,
      level: this.level,
      size: this.fireSize,
      owner: this
    })

    this.recentlyPlacedBomb = bomb
    const { x, y } = this.getTileCoords()
    Object.values(this.level.players).forEach(player => {
      if (player && player !== this) {
        const playerTileCoords = player.getTileCoords()
        if (playerTileCoords.x === x && playerTileCoords.y === y) {
          player.recentlyPlacedBomb = bomb
        }
      }
    })

    this.level.addChild(bomb)
  }

  stopMoving = () => {
    if (!this.alive) return
    super.stopMoving()
    this.stopKick()
    if (!this.movementEnabled) return
    this.textures = getAnimation(`player${this.playerId}Walk${this.direction}`)
    this.currentFrame = 1
    this.stop()
  }

  startMoving(direction: Direction): void {
    if (!this.movementEnabled) return
    if (!this.alive) return
    super.startMoving(direction)
    this.stopKick()
  }

  move() {
    if (!this.movementEnabled) return
    if (!this.alive) return
    super.move()
    this.processKick()
  }

  collide(entity: Entity, type: CollisionType): void {
    if (!this.alive) return
    super.collide(entity, type)

    if (type === CollisionType.HitBox) {
      if (entity instanceof PowerUp) {
        entity.removeFromLevel()
        if (!entity.alive) return
        switch (entity.type) {
          case "powerUpBomb": {
            this._powerUpCollection.powerUpCount.powerUpBomb++
            break
          }
          case "powerUpFire": {
            this._powerUpCollection.powerUpCount.powerUpFire++
            break
          }
          case "powerUpSkates": {
            this._powerUpCollection.powerUpCount.powerUpSkates++
            if (this._powerUpCollection.powerUpCount.powerUpSkates > 2) return
            // this.speed += PowerUp.SKATES_SPEED_INCREMENT
            break
          }
          case "powerUpKick": {
            this._powerUpCollection.powerUpCount.powerUpKick++
            break
          }
          case "powerUpPunch": {
            this._powerUpCollection.powerUpCount.powerUpPunch++
            break
          }
        }
      } else if (entity instanceof Poison) {
        entity.removeFromLevel()
        if (!entity.alive) return
        this.setPoisoned(entity.type)
      }
    } else {
      if (entity instanceof Bomb && this.hasKick && this.isFacingEntity(entity) && entity.canBeKicked(this.direction)) {
        this.startKick(entity)
      } else if (entity instanceof Player) {
        if (this.poison) {
          entity.setPoisoned(this.poison)
        } else if (entity.poison) {
          this.setPoisoned(entity.poison)
        }
      }
    }
  }

  explode(): void {
    // if (!this.level.isStarted) return
    if (!this.alive) return
    if (this.invulnerable) return

    super.explode()
    this.stopKick()

    const powerUpTypesList = this._powerUpCollection.getPowerupTypesList()
    const emptyTiles = this.level.getEmptyTilesCoordinates()
    for (let i = 0; i < powerUpTypesList.length; i++) {
      const randomTile = emptyTiles.splice(Math.floor(Math.random() * emptyTiles.length), 1)[0]
      this.level.addChild(new PowerUp({
        x: randomTile.x,
        y: randomTile.y,
        level: this.level,
        type: powerUpTypesList[i]
      }))
    }
    this.textures = getAnimation(`player${this.playerId}Die`)
    this.loop = false
    this.currentFrame = 0
    this.play()
    this.onComplete = () => this.removeFromLevel()
  }

  setPoisoned(type: PoisonEnum) {
    if (!this._poison) {
      this._poison = type
      this.startBlink(0x000000)
      Clock.setTimeout(() => {
        this._poison = null
        this.stopBlink()
      }, PowerUp.POISON_DURATION)
    }
  }

  toString() {
    return "Player"
  }
}
